
import React, { useState, useEffect, useMemo } from "react";
import { Plus, Minus } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { layerChangingAction } from "../../store/slice/LayerChangerSlice";
import { buildCountryLayers } from "../../utilities/layerStorage";
import type { CountryDetails } from "../../data/types";
import "./layers.css";

import baseMapImage from "../../assets/map/basemap.png";
import suitabilityImage from "../../assets/map/suitability.png";
import landUseImage from "../../assets/map/landuse.png";
import elevationImage from "../../assets/map/elevation.png";
import roadsImage from "../../assets/map/roads.png";

interface LayersComponentProps {
  onLayerSelect: (layer: string) => void;
  mapInstance?: any;
  selectedCountry?: string;
}


const LayersComponent: React.FC<LayersComponentProps> = ({ onLayerSelect, mapInstance, selectedCountry  }) => {
  const dispatch = useDispatch();
  const layerDetails = useSelector((state: any) => state.LayerChangerSlice.layerName);
  const countryDetails = useSelector((state: any) => state.SelectedCountrySlice.countryDetails as CountryDetails);

  // Generate dynamic layers from the payload
  const currentLayers = useMemo(() => buildCountryLayers(countryDetails), [countryDetails]);

  // Define layer configurations
  const layerConfigs = {
    'basemap': {
      layerName: "Base Map",
      value: null, // No additional layer, just base map
      type: "none",
      originalName: "basemap",
    },
    'suitability': {
      layerName: "Suitability",
      value: currentLayers.suitability,
      type: "raster",
      originalName: "suitability",
    },
    'landuse': {
      layerName: "Land Use",
      value: currentLayers.landuse,
      type: "raster",
      originalName: "landuse",
    },
    'elevation': {
      layerName: "Elevation",
      value: currentLayers.elevation,
      type: "raster",
      originalName: "elevation",
    },
    'urbanroads': {
      layerName: "Roads",
      value: currentLayers.urbanRoads,
      type: "raster",
      originalName: "urbanroads",
    }
  };

  const [selectedLayers, setSelectedLayers] = useState([]);

  // Sync selectedLayers with Redux store changes
  useEffect(() => {
    setSelectedLayers(layerDetails?.layerList || []);
  }, [layerDetails]);

  // Auto-select suitability layer on mount if no layers are selected
  useEffect(() => {
    if (mapInstance && (!selectedLayers || selectedLayers.length === 0)) {
      const suitabilityConfig = layerConfigs['suitability' as keyof typeof layerConfigs];

      // Check if suitability layer already exists on the map
      const existingLayers = mapInstance.getLayers().getArray();
      const alreadyExists = existingLayers.some(
        (layer: any) => layer.get && layer.get('layerName') === suitabilityConfig.layerName
      );

      if (!alreadyExists) {
        const initialLayer = [suitabilityConfig];

        setSelectedLayers(initialLayer);

        // Update Redux store
        const updatedLayerDetails = {
          layerList: initialLayer,
baseMapName: layerDetails?.baseMapName || "OSM_LAYER"
        };
        dispatch(layerChangingAction(updatedLayerDetails));

        // Add layer to map
        if (suitabilityConfig.value) {
          const newLayer = suitabilityConfig.value;
          newLayer.set('layerName', suitabilityConfig.layerName);
          mapInstance.addLayer(newLayer);
        }

        // Notify parent component
        onLayerSelect("suitability");
      }
    }
  }, [mapInstance]);

  const handleZoomIn = () => {
    if (mapInstance) {
      const view = mapInstance.getView();
      const currentZoom = view.getZoom();
      if (currentZoom !== undefined) {
        view.setZoom(currentZoom + 1);
      }
    }
  };

  const handleZoomOut = () => {
    if (mapInstance) {
      const view = mapInstance.getView();
      const currentZoom = view.getZoom();
      if (currentZoom !== undefined) {
        view.setZoom(currentZoom - 1);
      }
    }
  };

  const handleLayerSelect = (layer: string) => {
    onLayerSelect(layer);
  };



  // Reusable layer toggle function (single layer selection only)
  const toggleLayer = (layerKey: string) => {
    const layerConfig = layerConfigs[layerKey as keyof typeof layerConfigs];
    if (!layerConfig) return;

    const currentLayers = selectedLayers || [];
    const isLayerSelected = currentLayers.some(
      (l: any) => l.layerName === layerConfig.layerName
    );

    let updatedLayers;

    // Remove any existing layers first (single selection)
    if (mapInstance && currentLayers.length > 0) {
      currentLayers.forEach((existingLayer: any) => {
        const layers = mapInstance.getLayers().getArray();
        const layerToRemove = layers.find(layer =>
          layer.get && layer.get('layerName') === existingLayer.layerName
        );
        if (layerToRemove) {
          mapInstance.removeLayer(layerToRemove);
        }
      });
    }

    if (layerKey === 'basemap' || isLayerSelected) {
      // Base map selected or deselecting current layer - clear all layers
      updatedLayers = layerKey === 'basemap' ? [layerConfig] : [];
    } else {
      // Add the new layer (single selection)
      updatedLayers = [layerConfig];

      // Add to map if mapInstance is available and it's not base map
      if (mapInstance && layerConfig.value) {
        const newLayer = layerConfig.value;
        newLayer.set('layerName', layerConfig.layerName);
        mapInstance.addLayer(newLayer);
      }
    }

    setSelectedLayers(updatedLayers);

    // Update Redux store
    const updatedLayerDetails = {
      layerList: updatedLayers,
      baseMapName: layerDetails?.baseMapName || "OSM_LAYER"
    };
    dispatch(layerChangingAction(updatedLayerDetails));
  };

  return (
    <>
      <div className="layers-zoom-controls">
        <button className="layers-zoom-button" onClick={handleZoomIn}>
          <Plus size={16} />
        </button>
        <div className="layers-zoom-divider"></div>
        <button className="layers-zoom-button" onClick={handleZoomOut}>
          <Minus size={16} />
        </button>
      </div>

      <div className="layers-expanded-wrapper">
        <div className="layers-expanded-container">
          <h3 className="layers-expanded-title">Layers</h3>
          <div className="layers-expanded-content">
            <div
              className={`layers-map-item ${selectedLayers?.some((l: any) => l.layerName === "Base Map") ? 'layers-map-item--selected' : ''}`}
              onClick={() => {
                handleLayerSelect("infrastructure");
                toggleLayer("basemap");
              }}
            >
              <img
                src={baseMapImage}
                alt="Base Map"
                className="layers-map-image"
              />
              <span className="layers-map-text">Base Map</span>
            </div>
            <div
              className={`layers-map-item ${selectedLayers?.some((l: any) => l.layerName === "Suitability") ? 'layers-map-item--selected' : ''}`}
              onClick={() => {
                handleLayerSelect("suitability");
                toggleLayer("suitability");
              }}
            >
              <img
                src={suitabilityImage}
                alt="Suitability"
                className="layers-map-image"
              />
              <span className="layers-map-text">Suitability</span>
            </div>
            <div
              className={`layers-map-item ${selectedLayers?.some((l: any) => l.layerName === "Land Use") ? 'layers-map-item--selected' : ''}`}
              onClick={() => {
                handleLayerSelect("resources");
                toggleLayer("landuse");
              }}
            >
              <img
                src={landUseImage}
                alt="Land Use"
                className="layers-map-image"
              />
              <span className="layers-map-text">Land Use</span>
            </div>
            <div
              className={`layers-map-item ${selectedLayers?.some((l: any) => l.layerName === "Elevation") ? 'layers-map-item--selected' : ''}`}
              onClick={() => {
                handleLayerSelect("elevation");
                toggleLayer("elevation");
              }}
            >
              <img
                src={elevationImage}
                alt="Elevation"
                className="layers-map-image"
              />
              <span className="layers-map-text">Elevation</span>
            </div>
            <div
              className={`layers-map-item ${selectedLayers?.some((l: any) => l.layerName === "Roads") ? 'layers-map-item--selected' : ''}`}
              onClick={() => {
                handleLayerSelect("roads");
                toggleLayer("urbanroads");
              }}
            >
              <img
                src={roadsImage}
                alt="Roads"
                className="layers-map-image"
              />
              <span className="layers-map-text">Roads</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LayersComponent;
